import assign from "lodash/assign";
import concat from "lodash/concat";
import countBy from "lodash/countBy";
import filter from "lodash/filter";
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import findLast from "lodash/findLast";
import first from "lodash/first";
import forEach from "lodash/forEach";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import keyBy from "lodash/keyBy";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import toNumber from "lodash/toNumber";
import size from "lodash/size";
import some from "lodash/some";
import startsWith from "lodash/startsWith";
import moment from "moment";

import ChatActions from "../../actions/ChatActions";
import alt from "../../alt/ChatAlt";
import QuickReplyType from "../../constants/ChatAutoResponseQuickReplyTypes";
import * as chatFactory from "../../utils/chat/chatFactory";
import MessageType from "../../constants/ChatMessageTypes";
import chatNavigation from "../../utils/chat/chatNavigation";
import QuestionStore from "./QuestionStore";
import { formatStructuredText } from "../../utils/Utils";
import { triggerDefaultMessageEvent } from "../../utils/chat/EventHelper";

const { autoRateAcceptedAnswers } = window.companySettings;

export class ResponseStore {
    constructor() {
        this.bindActions(ChatActions);
        this.bindListeners({
            onReset: [ChatActions.askQuestion, ChatActions.resetChat],
        });
        this.error = undefined;
        this.isFirstLoad = true;
        this.isLoading = true;
        this.lastUpdated = null;
        this.lastViewedDateTime = moment.now();
        this.responses = [];
        this.responseSwitcherWasShownProgramatically = false;
        this.showResponseSwitcher = false;
    }

    onReset() {
        this.setState({
            error: undefined,
            isFirstLoad: true,
            isLoading: true,
            lastUpdated: null,
            lastViewedDateTime: null,
            responses: [],
            showResponseSwitcher: false,
            responseSwitcherWasShownProgramatically: false,
        });
    }

    onResponseAcceptSuccess({ question, responseId }) {
        const currentResponse = this.getResponseById(responseId);
        const rawResponse = find(question.response, { id: responseId });

        /**
         * Previously (before a bug was reported: DIR-7689) we were updating the
         * whole this.responses array with data from the backend, this update
         * process included even the messages. This made the messageList to
         * flicker.
         * The fix for this issue is below (as it is the code in the current
         * state). We don't need to update the whole this.responses array when
         * the user accepts an answer, we only need to update the current
         * response and only parts of it.
         */
        currentResponse.dateCreated = rawResponse.dateCreated;
        currentResponse.happiness = rawResponse.happiness;
        currentResponse.happinessLastUpdated = rawResponse.happinessLastUpdated;
        currentResponse.isAccepted = rawResponse.isAccepted;
        currentResponse.isViewedByUser = rawResponse.isViewedByUser;
        currentResponse.lastUpdated = rawResponse.lastUpdated;
        currentResponse.testimonialDateCreated = rawResponse.testimonialDateCreated;
        if (!autoRateAcceptedAnswers && currentResponse.happiness === "not-rated") {
            currentResponse.isLeavingRating = true;
        }
        currentResponse.messages = this.buildMessagesForResponse(rawResponse);
    }

    onLoadSuccess({ question }) {
        this.setState({
            error: undefined,
            isLoading: false,
            isFirstLoad: false,
            lastViewedDateTime: chatNavigation.isUserActive ? moment.now() : this.lastViewedDateTime,
            responses: this.buildResponsesForQuestion(question),
            showResponseSwitcher: this.shouldShowResponseSwitcher(question),
        });
    }

    onLoadFail(error) {
        this.setState({ error, isLoading: false });
    }

    onAddNewMessage({ id, messageText, responseId }) {
        const currentResponse = this.getResponseById(responseId);
        const author = chatFactory.buildAuthor({ isOriginalPoster: true });
        const message = chatFactory.buildMessage({
            author,
            id,
            isLoading: true,
            messageText,
            responseId: currentResponse.id,
            type: MessageType.ResponseMessage,
        });

        currentResponse.messages.push(message);
    }

    onAddNewMessageSuccess({ message, tempId, questionUuid }) {
        const currentResponse = this.getResponseById(message.responseId);
        const messageIndex = findIndex(currentResponse.messages, { id: tempId });
        const posterMessage = currentResponse.messages[messageIndex];
        if (!posterMessage) {
            return;
        }

        posterMessage.dateCreated = message.dateCreated;
        posterMessage.id = message.id;
        posterMessage.uuid = message.uuid;
        posterMessage.lastUpdated = message.lastUpdated;
        posterMessage.messageText = formatStructuredText(message.body);
        posterMessage.isLoading = false;

        triggerDefaultMessageEvent({
            message: posterMessage,
            response: currentResponse,
            question: { uuid: questionUuid },
        });
    }

    onAddNewMessageFail() {
        // this.emit("change");
    }

    onUploadAttachment({ tempId: attachmentTempId, responseId }) {
        const currentResponse = this.getResponseById(responseId);
        const author = chatFactory.buildAuthor({ isOriginalPoster: true });
        const message = chatFactory.buildMessage({
            author,
            id: attachmentTempId,
            isLoading: true,
            messageText: i18n.t("rtm.text.uploading"),
            responseId: currentResponse.id,
            type: MessageType.ResponseMessage,
        });

        currentResponse.messages.push(message);
    }

    onUploadAttachmentSuccess({ body, dateCreated, id, lastUpdated, responseId, tempId }) {
        const currentResponse = this.getResponseById(responseId);
        const messageIndex = findIndex(currentResponse.messages, { id: tempId });

        currentResponse.messages[messageIndex].dateCreated = dateCreated;
        currentResponse.messages[messageIndex].id = id;
        currentResponse.messages[messageIndex].lastUpdated = lastUpdated;
        currentResponse.messages[messageIndex].messageText = formatStructuredText(body);
        currentResponse.messages[messageIndex].isLoading = false;
    }

    onResponseRate() {
        const response = this.getActualCurrentResponse();

        response.ratingInProgress = true;
    }

    onResponseRateFail({ prevHappiness }) {
        const response = this.getActualCurrentResponse();

        response.happiness = prevHappiness;
        response.ratingInProgress = false;
    }

    onResponseRateSuccess({ question }) {
        this.responses = this.buildResponsesForQuestion(question);
        this.isLoading = false;
        this.lastUpdated = null;
        this.getActualCurrentResponse().ratingInProgress = false;
    }

    onRerouteSuccess({ question }) {
        this.responses = this.buildResponsesForQuestion(question);
    }

    onUpdateContactSuccess({ question }) {
        this.setState({
            error: undefined,
            isLoading: false,
            isFirstLoad: false,
            lastViewedDateTime: chatNavigation.isUserActive ? moment.now() : this.lastViewedDateTime,
            responses: this.buildResponsesForQuestion(question),
            showResponseSwitcher: this.shouldShowResponseSwitcher(question),
        });
    }

    onMarkResponseAsRead() {
        const response = this.getActualCurrentResponse();

        response.isViewedByUser = true;
    }

    onMarkResponseAsReadFail() {
        const response = this.getActualCurrentResponse();

        response.isViewedByUser = false;
    }

    onResponseAddTestimonial() {
        const response = this.getActualCurrentResponse();

        response.testimonialDateCreated = moment().toISOString();
    }

    onResponseAddTestimonialFail() {
        const response = this.getActualCurrentResponse();

        response.testimonialDateCreated = null;
    }

    onResponseAddTestimonialSuccess({ question }) {
        this.responses = this.buildResponsesForQuestion(question);
    }

    onResponseToggleRateToolbar() {
        const response = this.getActualCurrentResponse();

        response.isLeavingRating = !response.isLeavingRating;
    }

    onResponseToggleTestimonialForm() {
        const response = this.getActualCurrentResponse();

        response.isLeavingTestimonial = !response.isLeavingTestimonial;
    }

    onAutoResponseReplySuccess({ question }) {
        this.responses = this.buildResponsesForQuestion(question);
    }

    onReviewMarkAsResolvedSuccess({ confirm, question }) {
        const currentResponse = this.getActualCurrentResponse();

        this.responses = this.buildResponsesForQuestion(question);
        if (confirm && !autoRateAcceptedAnswers && currentResponse.happiness === "not-rated") {
            currentResponse.isLeavingRating = true;
        }
    }

    onShowResponseSwitcher() {
        this.setState({ showResponseSwitcher: true });
    }

    onHideResponseSwitcher() {
        this.setState({
            showResponseSwitcher: false,
            responseSwitcherWasShownProgramatically: true,
        });
    }

    buildResponsesForQuestion(question) {
        return question.response.map(rawResponse => {
            const response = assign({}, rawResponse, {
                isLeavingRating: get(this.getResponseById(rawResponse.id), "isLeavingRating", false),
                isLeavingTestimonial: get(this.getResponseById(rawResponse.id), "isLeavingTestimonial", false),
                ratingInProgress: get(this.getResponseById(rawResponse.id), "ratingInProgress", false),
                isViewedByUser: this.isResponseViewedByUser(rawResponse),
                messages: this.buildMessagesForResponse(rawResponse),
                responseMessageAuthor: rawResponse.responseMessageAuthor
                    ? chatFactory.buildAuthor(rawResponse.responseMessageAuthor)
                    : undefined,
            });

            return chatFactory.buildResponse(response);
        });
    }

    buildMessagesForResponse(rawResponse) {
        const messagesByAuthor = groupBy(rawResponse.messages, "author.id");
        const responseMessages = rawResponse.messages.map(rawMessage =>
            chatFactory.buildMessage(
                assign(
                    {
                        actions: rawMessage.actions,
                        author: chatFactory.buildAuthor(rawMessage.author),
                        dateCreated: rawMessage.dateCreated,
                        id: rawMessage.id,
                        uuid: rawMessage.uuid,
                        isFirstResponseMessage: this.isFirstResponseMessageOfAuthor(
                            messagesByAuthor,
                            rawMessage.author.id,
                            rawMessage.id
                        ),
                        lastUpdated: rawMessage.lastUpdated,
                        messageText: rawMessage.body,
                        messageTextHtml: rawMessage.bodyHtml,
                        quickReply: rawMessage.quickReply,
                        responseId: rawResponse.id,
                        type: rawMessage.type,
                        viewedByUser: rawMessage.viewedByUser,
                    },
                    rawMessage.type === "MarkAsResolvedMessage"
                        ? {
                              customerRepliedToMarkAsResolved: rawMessage.customerRepliedToMarkAsResolved,
                              resolved: rawMessage.resolved,
                          }
                        : {}
                )
            )
        );
        /*
         * We want to keep temporary messages (built using onAddNewMessage()) in
         * store, so that onAddNewMessage() will have what to update when it's
         * called.
         */
        const currentResponseInStore = this.getResponseById(rawResponse.id);
        const currentMessagesInStore = get(currentResponseInStore, "messages", []);
        const tempMessagesInStore = filter(
            currentMessagesInStore,
            message => startsWith(message.id, "tempMessageId_") || startsWith(message.id, "tempAttachmentId_")
        );

        return concat(responseMessages, tempMessagesInStore);
    }

    isResponseViewedByUser(rawResponse) {
        const currentResponse = this.getActualCurrentResponse();
        const currentResponseId = get(currentResponse, "id");

        /*
         * don't wait on the heartbeat to mark as viewed the currently selected
         * response.
         */
        return currentResponseId === rawResponse.id ? true : rawResponse.isViewedByUser;
    }

    getActualCurrentResponse() {
        const responseId = this.getInstance().getCurrentResponseId();
        const responseIndexInStore = findIndex(this.responses, { id: responseId });

        return this.responses[responseIndexInStore];
    }

    getResponseById(responseId) {
        const responseIndexInStore = findIndex(this.responses, { id: toNumber(responseId) });

        return this.responses[responseIndexInStore];
    }

    isFirstResponseMessageOfAuthor(messagesByAuthor, authorId, iteratedMessageId) {
        const messagesOfAuthor = messagesByAuthor[authorId];
        const firstMessageOfAuthor = first(messagesOfAuthor);

        return firstMessageOfAuthor.id === iteratedMessageId;
    }

    shouldShowResponseSwitcher(rawQuestion) {
        this.waitFor(QuestionStore);

        const nextResponses = this.buildResponsesForQuestion(rawQuestion);
        const questionIsOneOnOne = QuestionStore.isOneOnOne();
        const shouldShowResponseSwitcherOnFirstAnswers = this.shouldShowResponseSwitcherOnFirstAnswers(nextResponses);
        const shouldShowResponseSwitcherOnUnreadMessages = this.shouldShowResponseSwitcherOnUnreadMessages(
            nextResponses
        );

        if (shouldShowResponseSwitcherOnFirstAnswers || shouldShowResponseSwitcherOnUnreadMessages) {
            this.setState({ responseSwitcherWasShownProgramatically: true });
        }

        return (
            !questionIsOneOnOne &&
            (this.showResponseSwitcher ||
                shouldShowResponseSwitcherOnFirstAnswers ||
                shouldShowResponseSwitcherOnUnreadMessages)
        );
    }

    shouldShowResponseSwitcherOnFirstAnswers(nextResponses) {
        const nextAnswerCountForResponses = this.getInstance().getAnswerCountForResponses(nextResponses);
        const currentAnswerCountForResponses = this.getInstance().getAnswerCountForResponses();
        const newResponsesCameIn = currentAnswerCountForResponses === 0 && nextAnswerCountForResponses > 0;
        const showResponseSwitcherOnFirstAnswers =
            !this.isLoading && newResponsesCameIn && !this.responseSwitcherWasShownProgramatically;

        return showResponseSwitcherOnFirstAnswers;
    }

    shouldShowResponseSwitcherOnUnreadMessages(nextResponses) {
        const currentResponseId = this.getInstance().getCurrentResponseId();
        const unreadResponses = filter(
            nextResponses,
            response => response.id !== currentResponseId && response.isViewedByUser === false
        );
        const unreadResponsesWithAnswers = forEach(unreadResponses, response =>
            some(response.message, { type: MessageType.Answer })
        );
        const hasUnreadResponses = size(unreadResponsesWithAnswers) > 0;
        const showResponseSwitcherOnUnreadMessages =
            hasUnreadResponses && !this.responseSwitcherWasShownProgramatically;

        return showResponseSwitcherOnUnreadMessages;
    }

    static getCurrentResponseId() {
        const { responseId } = chatNavigation.location.query;

        return toNumber(responseId);
    }

    static getResponses() {
        return this.getState().responses.sort((a, b) => a.id - b.id);
    }

    static getCurrentResponse() {
        const { responses } = this.getState();
        let currentResponse = {};

        if (size(responses) > 0) {
            const responseId = this.getCurrentResponseId();

            currentResponse = responseId ? find(responses, { id: responseId }) : first(responses);
        }

        return currentResponse;
    }

    static getMessagesForCurrentResponse() {
        const currentResponse = this.getCurrentResponse();
        const responseCount = this.getResponseCount();
        const messages = responseCount > 0 ? get(currentResponse, "messages", []) : QuestionStore.getMessages();

        return messages.filter(message => includes(MessageType, message.type));
    }

    static getIndexOfResponse(responseId) {
        return findIndex(this.getState().responses, { id: toNumber(responseId) });
    }

    static getResponseCount() {
        return this.getState().responses.length;
    }

    static getAutoResponseMessage() {
        const currentResponse = this.getCurrentResponse();

        return (
            !isEmpty(currentResponse) &&
            some(currentResponse.messages, { type: MessageType.AutoResponseMessage }) &&
            find(currentResponse.messages, { type: MessageType.AutoResponseMessage })
        );
    }

    static hasNewMarkAsResolvedMessage() {
        const currentResponse = this.getCurrentResponse();
        const markAsResolvedMessage = this.getMarkAsResolvedMessage();
        return (
            !isEmpty(currentResponse) &&
            !isEmpty(markAsResolvedMessage) &&
            !markAsResolvedMessage.customerRepliedToMarkAsResolved
        );
    }

    static getMarkAsResolvedMessage() {
        return findLast(this.getCurrentResponse().messages, { type: MessageType.MarkAsResolvedMessage });
    }

    static hasPendingAutoResponse() {
        return (
            this.getCurrentResponse().quickReply === QuickReplyType.PENDING ||
            this.getCurrentResponse().quickReply === QuickReplyType.OPEN_EXPIRED
        );
    }

    static hasAcceptedAutoResponse() {
        return this.getCurrentResponse().quickReply === QuickReplyType.ACCEPTED;
    }

    static hasRejectedAutoResponse() {
        return this.getCurrentResponse().quickReply === QuickReplyType.REJECTED;
    }

    static hasTerminalAutoResponse() {
        return this.getCurrentResponse().quickReply === QuickReplyType.TERMINAL;
    }

    static allResponsesHaveAnswers() {
        return some(this.getState().responses, response => some(response.messages, { type: MessageType.Answer }));
    }

    static getLatestAnswerForResponse(response) {
        const messages = get(response, "messages", []);

        return findLast(
            messages,
            message => message.type === MessageType.Answer || message.type === MessageType.AutoResponseMessage
        );
    }

    static getLatestAnswerForCurrentResponse() {
        const currentResponse = this.getCurrentResponse();

        return this.getLatestAnswerForResponse(currentResponse);
    }

    static getLatestAnswerForAllResponses() {
        const responses = this.getResponses();
        const answers = map(responses, this.getLatestAnswerForResponse);

        return keyBy(answers, "responseId");
    }

    static getUnreadResponses() {
        const responses = this.getResponses();

        return filter(responses, { isViewedByUser: false });
    }

    static getAnswerCountForResponses(responses = this.getState().responses) {
        return countBy(responses, response => some(response.messages, { type: MessageType.Answer })).true || 0;
    }

    static rerouteSystemMessageHasCustomRerouteLink() {
        const messages = this.getMessagesForCurrentResponse();
        const rerouteSystemMessage = find(messages, { type: MessageType.QuestionReroutedSystemMessage });
        const rerouteSystemMessageActions = get(rerouteSystemMessage, "actions", []);

        return !isEmpty(rerouteSystemMessageActions);
    }

    /**
     * Superhack! Getting the autoresponse rejected message so we can use its
     * date to build the fake reply from the bot on the frontend.
     * This will become obsolete once the system messages will be moved to the
     * backend.
     */
    static getAutoResponseRejectedMessage() {
        const messages = this.getMessagesForCurrentResponse();

        return find(messages, { messageText: "No, I still need help." });
    }
}

export default alt.createStore(ResponseStore, "ResponseStore");
